/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import {
	Box,
	Group,
	Title,
	Radio as NativeRadio,
	Tooltip,
} from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './EditProducerForm.module.css';

// Utils
import {
	cleanObject,
	convertArrayToLabelValue,
	convertUsersToLabelValue,
} from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { TextInput } from '@components/atoms/TextInput';
import { useTranslation } from 'react-i18next';
import {
	Divider,
	Radio,
	TextInputV2,
} from '@components/atoms';
import { useMediaQuery } from '@mantine/hooks';
import {
	matches,
	useForm,
	UseFormReturnType,
} from '@mantine/form';
import {
	TFormValuesContact,
	TFormValuesProducer,
} from '@features/resources/producers/types';
import { useUsers } from '@api/get-users';
import { useAddUser } from '@api/create-user';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { useAllOrganizations } from 'shared/api/get-all-organizations';
import { NumberInput } from '@components/atoms/NumberInput';
import { ALL_COUNTRIES } from '@config/constants/all-countries';
import { useUpdateProducer } from '@features/resources/producers/api/update-producer';
import { PictureUpload } from '@components/molecules/PictureUpload';
import { useUploadPicture } from '@api/upload-picture';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const EditProducerForm = (props: {
	form: UseFormReturnType<TFormValuesProducer>;
	onCancel: () => void;
	onBack: () => void;
	open: boolean;
}) => {
	const [addContactType, setAddContactType] = useState(
		'existingContact'
	);

	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const [ownerId, setOwnerId] = useState<null | string>(
		null
	);

	const {
		producersPage,
		producersPageSize,
		producersSelectedRow,
		setProducerOrganization,
	} = useStore();

	const { t } = useTranslation();

	const { data: organizations } = useAllOrganizations();

	const { data: users } = useUsers(
		{ enabled: props.open },
		{
			organization: props.form.getValues().organization,
		}
	);

	const uploadPictureMutation = useUploadPicture({
		mutationConfig: {
			onSuccess(data) {
				const {
					name,
					organization,
					countryCode,
					place,
					contact,
				} = props.form.getValues();

				updateProducer.mutate({
					data: {
						...(props.form.isDirty('contact')
							? {
									contact,
								}
							: {}),
						...(props.form.isDirty('name')
							? {
									name,
								}
							: {}),
						...(props.form.isDirty('organization')
							? {
									organization,
								}
							: {}),

						...(props.form.isDirty('logo')
							? {
									logo: data.id,
								}
							: {}),
						...(props.form.isDirty('countryCode') ||
						props.form.isDirty('place')
							? {
									location: {
										countryCode: countryCode,
										place: place,
									},
								}
							: {}),
					},
					id: producersSelectedRow.id,
				});
			},
		},
	});

	const updateProducer = useUpdateProducer({
		page: producersPage,
		size: producersPageSize,
		mutationConfig: {
			onSuccess() {
				props.onBack();
			},
		},
	});

	const addUser = useAddUser({
		page: producersPage,
		size: producersPageSize,
		mutationConfig: {
			onSuccess(data) {
				setOwnerId(data.id);
				if (pictureData) {
					uploadPictureMutation.mutate(pictureData);
				} else {
					const {
						name,
						organization,
						countryCode,
						place,
					} = props.form.getValues();

					updateProducer.mutate({
						data: cleanObject({
							...(props.form.isDirty('name')
								? {
										name,
									}
								: {}),
							...(props.form.isDirty('organization')
								? {
										organization,
									}
								: {}),
							...(props.form.isDirty('organization')
								? {
										organization,
									}
								: {}),
							contact: data.id,
							...(props.form.isDirty('countryCode') ||
							props.form.isDirty('place')
								? {
										location: {
											countryCode: countryCode,
											place: place,
										},
									}
								: {}),
						}),
						id: producersSelectedRow.id,
					});
				}
			},
		},
	});

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			if (
				props.form.getValues().logo !== null &&
				props.form.isDirty('logo') &&
				props.form.isValid()
			) {
				uploadPictureMutation.mutate(pictureData);
			} else {
				props.form.validate();
				if (props.form.isValid()) {
					const {
						name,
						organization,
						countryCode,
						place,
						contact,
						obfuscateIPFS,
					} = props.form.values;

					updateProducer.mutate({
						data: {
							...(props.form.isDirty('logo')
								? {
										logo: null,
									}
								: {}),
							...(props.form.isDirty('name')
								? {
										name,
									}
								: {}),
							...(props.form.isDirty('organization')
								? {
										organization,
									}
								: {}),
							...(props.form.isDirty('contact')
								? {
										contact,
									}
								: {}),
							...(props.form.isDirty('obfuscateIPFS')
								? {
										obfuscateIPFS:
											obfuscateIPFS === 'true',
									}
								: {}),
							...(props.form.isDirty('countryCode') ||
							props.form.isDirty('place')
								? {
										location: {
											countryCode: countryCode,
											place: place,
										},
									}
								: {}),
						},
						id: producersSelectedRow.id,
					});
				}
			}
		};

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.form.getInputProps('organization');

		const handleChangeOrganization = (_id: string) => {
			props.form.setFieldValue('organization', _id);
			if (_id) setProducerOrganization(_id);
			props.form.setFieldValue('contact', null);
		};

		/* ******************************** Media Query ******************************* */
		const isBigScreen = useMediaQuery(
			'(min-width: 120em)'
		);

		const handleOnUploadProfile = (
			formData: FormData | string | null
		) => {
			if (typeof formData !== 'string' && formData) {
				props.form.setFieldValue('logo', formData);
				setPictureData(formData);
			} else {
				props.form.setFieldValue('logo', null);
			}
		};

		// Validators
		const formContact = useForm<TFormValuesContact>({
			initialValues: {
				firstName: '',
				lastName: '',
				email: '',
				language: 'fr',
				phoneNumber: '',
			},
			validate: {
				firstName: (value) =>
					value.length < 2
						? t('validations.contact.firstName')
						: null,
				lastName: (value) =>
					value.length < 2
						? t('validations.contact.lastName')
						: null,

				email: matches(
					REGEX_EMAIL,
					'form.error.invalidEmail'
				),
				phoneNumber: matches(
					REGEX_PHONE_NUMBER,
					'form.error.invalidPhoneNumber'
				),
			},
		});
		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(() => {})}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{props.form.getValues().name
							? props.form.getValues().name
							: t('global.producers')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('producers.updateProducer')}
					</Text>
				</Box>

				<PictureUpload
					onUpload={handleOnUploadProfile}
					src={producersSelectedRow?.logo || ''}
				/>

				{/* Row for Name */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							visibleFrom="xxl"
							size="md"
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								organizations
							)}
							placeholder={t('global.organization')}
							onChange={handleChangeOrganization}
							disabled
							{...organizationProps}
						/>
						<Select
							hiddenFrom="xxl"
							size="sm"
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								organizations
							)}
							placeholder={t('global.organization')}
							disabled
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
					</Box>
				</Box>
				<Divider size="xs" />
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							withAsterisk
							visibleFrom="xxl"
							size="md"
							label={t('global.name')}
							placeholder={t('global.name')}
							{...props.form.getInputProps('name')}
						/>
						<TextInput
							withAsterisk
							hiddenFrom="xxl"
							size="sm"
							label={t('global.name')}
							placeholder={t('global.name')}
							{...props.form.getInputProps('name')}
						/>
					</Box>
				</Box>
				{/* Row for Country & Place */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.country')}
							data={ALL_COUNTRIES}
							placeholder={t('placeholders.country')}
							{...props.form.getInputProps('countryCode')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							disabled={!props.form.values.countryCode}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.place')}
							placeholder={t('global.place')}
							{...props.form.getInputProps('place')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Tooltip label={t('global.comingSoon')}>
						<Box className={classes.containerInput}>
							<NumberInput
								disabled
								hideControls
								maxLength={9}
								withAsterisk
								visibleFrom="xxl"
								size="md"
								label={t('global.legalIdentifier')}
								placeholder={t('global.legalIdentifier')}
								{...props.form.getInputProps(
									'legalIdentifier'
								)}
							/>
							<NumberInput
								disabled
								hideControls
								maxLength={9}
								withAsterisk
								hiddenFrom="xxl"
								size="sm"
								label={t('global.legalIdentifier')}
								placeholder={t('global.legalIdentifier')}
								{...props.form.getInputProps(
									'legalIdentifier'
								)}
							/>
						</Box>
					</Tooltip>
				</Box>
				<Divider size="xs" />
				<Box className={classes.row}>
					<Text className={classes.titleAddContact}>
						{t('global.contact')}
					</Text>
				</Box>
				<>
					{producersSelectedRow?.contact?.id ? (
						<Box className={classes.row}>
							<Box className={classes.containerInput}>
								<Select
									disabled={producersSelectedRow?.contact}
									visibleFrom="xxl"
									size="md"
									label={t('global.contact')}
									withAsterisk
									data={convertUsersToLabelValue(users)}
									placeholder={t('global.contact')}
									{...props.form.getInputProps('contact')}
								/>
								<Select
									disabled={producersSelectedRow?.contact}
									hiddenFrom="xxl"
									size="sm"
									label={t('global.contact')}
									withAsterisk
									data={convertUsersToLabelValue(users)}
									placeholder={t('global.contact')}
									{...props.form.getInputProps('contact')}
								/>
							</Box>
						</Box>
					) : (
						<>
							<Box className={classes.row}>
								<NativeRadio.Group
									value={addContactType}
									name="addProject"
									withAsterisk
									onChange={setAddContactType}
								>
									<Group gap={'47px'}>
										<Radio
											disabled={
												producersSelectedRow?.contact
											}
											variant="outline"
											size={isBigScreen ? 'sm' : 'xs'}
											label={t(
												'global.selectAnExistingContact'
											)}
											value={'existingContact'}
										/>
										<Radio
											disabled={
												producersSelectedRow?.contact
											}
											variant="outline"
											size={isBigScreen ? 'sm' : 'xs'}
											label={t('global.addNewContact')}
											value={'newContact'}
										/>
									</Group>
								</NativeRadio.Group>
							</Box>
							{addContactType == 'existingContact' ? (
								<Box className={classes.row}>
									<Box className={classes.containerInput}>
										<Select
											disabled={
												producersSelectedRow?.contact
											}
											visibleFrom="xxl"
											size="md"
											label={t('global.contact')}
											withAsterisk
											data={convertUsersToLabelValue(
												users
											)}
											placeholder={t('global.contact')}
											{...props.form.getInputProps(
												'contact'
											)}
										/>
										<Select
											disabled={
												producersSelectedRow?.contact
											}
											hiddenFrom="xxl"
											size="sm"
											label={t('global.contact')}
											withAsterisk
											data={convertUsersToLabelValue(
												users
											)}
											placeholder={t('global.contact')}
											{...props.form.getInputProps(
												'contact'
											)}
										/>
									</Box>
								</Box>
							) : (
								<>
									<Box className={classes.row}>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												disabled={
													!props.form.getValues()
														.organization
												}
												maxLength={100}
												withAsterisk
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.firstName')}
												placeholder={t(
													'global.firstName'
												)}
												{...formContact.getInputProps(
													'firstName'
												)}
											/>
										</Box>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												disabled={
													!props.form.getValues()
														.organization
												}
												maxLength={100}
												withAsterisk
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.lastName')}
												placeholder={t('global.lastName')}
												{...formContact.getInputProps(
													'lastName'
												)}
											/>
										</Box>
									</Box>
									<Box className={classes.row}>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												disabled={
													!props.form.getValues()
														.organization
												}
												withAsterisk
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.email')}
												placeholder={t('global.email')}
												{...formContact.getInputProps(
													'email'
												)}
											/>
										</Box>
									</Box>
									<Box className={classes.row}>
										<Box
											style={{
												width: '30%',
											}}
										>
											<NativeRadio.Group
												withAsterisk
												label={t('global.language')}
												{...formContact.getInputProps(
													'language'
												)}
											>
												<Group gap={'25px'} pt={'md'}>
													<NativeRadio
														disabled={
															!props.form.getValues()
																.organization
														}
														variant="outline"
														size={
															isBigScreen ? 'sm' : 'xs'
														}
														label={'Fr'}
														value={'fr'}
													/>
													<NativeRadio
														disabled={
															!props.form.getValues()
																.organization
														}
														variant="outline"
														size={
															isBigScreen ? 'sm' : 'xs'
														}
														label={'En'}
														value={'en'}
													/>
												</Group>
											</NativeRadio.Group>
										</Box>
										<Box
											className={classes.containerInput}
										>
											<TextInputV2
												disabled={
													!props.form.getValues()
														.organization
												}
												size={isBigScreen ? 'md' : 'sm'}
												label={t('global.phoneNumber')}
												placeholder={t(
													'global.phoneNumber'
												)}
												{...formContact.getInputProps(
													'phoneNumber'
												)}
											/>
										</Box>
									</Box>
								</>
							)}
						</>
					)}
				</>

				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							hiddenFrom="xxl"
							size="md"
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							fullWidth
							size="lg"
							variant="transparent"
							visibleFrom="xxl"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							disabled={
								(!props.form.isDirty() &&
									(addContactType == 'newContact'
										? !formContact.isDirty()
										: true)) ||
								updateProducer.isLoading
							}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={() => {
								if (
									addContactType !== 'existingContact'
								) {
									props.form.validate();
									formContact.validate();

									if (formContact.isValid()) {
										const values =
											formContact.getValues();
										const { phoneNumber, ...restValues } =
											values;
										addUser.mutate({
											...(!isNil(
												props.form.getValues()
													.organization
											)
												? {
														organization:
															props.form.getValues()
																.organization,
													}
												: {}),
											...restValues,
											...(phoneNumber !== ''
												? {
														phoneNumber,
													}
												: {}),
										});
									}
								} else {
									handleSubmitForm();
								}
							}}
							loading={updateProducer.isLoading}
						>
							{t('producers.updateProducer')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
